import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sun, Moon, Star, Heart, ChatCircle, Sparkle } from "@phosphor-icons/react";
import Navbar from "../components/Navbar";
import MoonWidget from "../components/MoonWidget";
import TopMatchToday from "../components/TopMatchToday";
import WeeklyDigest from "../components/WeeklyDigest";
import NotificationCenter from "../components/NotificationCenter";
import { authFetch, useAuth } from "../context/AuthContext";

const SIGN_GLYPH = {
    Aries: "♈", Taurus: "♉", Gemini: "♊", Cancer: "♋", Leo: "♌", Virgo: "♍",
    Libra: "♎", Scorpio: "♏", Sagittarius: "♐", Capricorn: "♑", Aquarius: "♒", Pisces: "♓",
};

export default function Dashboard() {
    const { user } = useAuth();
    const [topMatches, setTopMatches] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [horoscope, setHoroscope] = useState(null);
    const [loadingMatches, setLoadingMatches] = useState(true);
    const [loadingH, setLoadingH] = useState(true);

    useEffect(() => {
        if (!user?.has_birth_info) return;
        // Top picks
        authFetch("get", "/matches/feed")
            .then((r) => setTopMatches((r.data.matches || []).slice(0, 3)))
            .finally(() => setLoadingMatches(false));
        // Recent conversations
        authFetch("get", "/messages/inbox/list")
            .then((r) => setConversations((r.data.conversations || []).slice(0, 3)))
            .catch(() => {});
        // Daily horoscope
        authFetch("post", "/horoscope", { period: "daily" })
            .then((r) => setHoroscope(r.data.text))
            .finally(() => setLoadingH(false));
    }, [user]);

    if (!user) return null;
    const unreadTotal = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);

    return (
        <>
            <Navbar />
            <main className="min-h-screen pt-28 pb-24 px-4 sm:px-6">
                <div className="max-w-6xl mx-auto space-y-6">
                    {/* WELCOME BANNER - compact, signature inline */}
                    <motion.section
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 pt-2"
                    >
                        <div>
                            <p className="label-tracking text-gold">Welcome back</p>
                            <h1 className="font-serif text-4xl sm:text-5xl mt-1" data-testid="dashboard-greeting">{user.name}</h1>
                            <p className="text-muted text-sm mt-2 font-serif">
                                {SIGN_GLYPH[user.sun]} {user.sun} Sun &nbsp;·&nbsp;
                                {SIGN_GLYPH[user.moon]} {user.moon} Moon &nbsp;·&nbsp;
                                {SIGN_GLYPH[user.rising]} {user.rising} Rising
                            </p>
                        </div>
                        <Link to="/matches" className="btn-gold-solid inline-flex items-center gap-2 self-start sm:self-end" data-testid="hero-discover-btn">
                            <Heart size={16} weight="fill" /> Discover
                        </Link>
                    </motion.section>

                    {/* NOTIFICATION CENTER — aggregated cosmic signals */}
                    <NotificationCenter />

                    {/* TODAY'S TOP MATCH - "Your most aligned soul today" */}
                    <TopMatchToday />

                    {/* WEEKLY COSMIC DIGEST - top 3 matches + upcoming moon shift */}
                    <WeeklyDigest />

                    {/* MOON WIDGET */}
                    <MoonWidget />

                    {/* TOP MATCHES - the dating hero */}
                    <motion.section
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        data-testid="top-matches-section"
                    >
                        <div className="flex items-baseline justify-between mb-3">
                            <div>
                                <p className="label-tracking text-gold">Today's Cosmic Picks</p>
                                <h2 className="font-serif text-2xl sm:text-3xl mt-1">Souls aligned with yours</h2>
                            </div>
                            <Link to="/matches" className="label-tracking text-muted hover:text-gold transition-colors text-xs" data-testid="see-all-matches">
                                See All →
                            </Link>
                        </div>

                        {loadingMatches && <div className="glass p-8 text-muted text-center">Aligning stars...</div>}

                        {!loadingMatches && topMatches.length === 0 && (
                            <div className="glass p-8 text-center">
                                <p className="font-serif text-xl">The constellations are still gathering</p>
                                <p className="text-muted text-sm mt-2">Check back soon as more souls join SoulStar.</p>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {topMatches.map((m, i) => (
                                <motion.div
                                    key={m.user_id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.1 * i }}
                                >
                                    <Link to={`/compatibility/${m.user_id}`} className="glass-gold p-5 hover-lift block group" data-testid={`top-match-${m.user_id}`}>
                                        <div className="flex items-start gap-3">
                                            <div className="w-14 h-14 rounded-full bg-white/5 border border-gold/30 flex items-center justify-center text-2xl text-gold flex-shrink-0">
                                                {m.picture ? (
                                                    <img src={m.picture} alt="" className="w-full h-full rounded-full object-cover" />
                                                ) : (
                                                    <span className="font-serif">{SIGN_GLYPH[m.sun]}</span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-serif text-xl truncate">{m.name}</h3>
                                                <p className="text-xs text-muted truncate">{m.birth_location}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-serif text-3xl text-gold leading-none">{m.compatibility.overall}</p>
                                                <p className="label-tracking text-[10px] text-muted">score</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-1.5 mt-3">
                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10">{SIGN_GLYPH[m.sun]} {m.sun}</span>
                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10">{SIGN_GLYPH[m.moon]} {m.moon}</span>
                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10">{SIGN_GLYPH[m.rising]} {m.rising}</span>
                                        </div>
                                        <p className="text-xs text-muted mt-3 italic line-clamp-2 font-serif">
                                            {m.compatibility.sun_compat.description}
                                        </p>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>
                    </motion.section>

                    {/* CONVERSATIONS + HOROSCOPE side-by-side on desktop */}
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                        {/* Conversations */}
                        <motion.section
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="glass p-6 lg:col-span-2"
                            data-testid="recent-messages-section"
                        >
                            <div className="flex items-baseline justify-between mb-4">
                                <div>
                                    <p className="label-tracking text-gold flex items-center gap-2">
                                        <ChatCircle size={14} weight="duotone" /> Conversations
                                        {unreadTotal > 0 && (
                                            <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-br from-[#E6C387] to-[#F5D68F] text-[#03030A] text-[10px] font-semibold">
                                                {unreadTotal}
                                            </span>
                                        )}
                                    </p>
                                    <h3 className="font-serif text-2xl mt-1">Messages</h3>
                                </div>
                                <Link to="/messages" className="text-xs label-tracking text-muted hover:text-gold transition-colors">All →</Link>
                            </div>

                            {conversations.length === 0 ? (
                                <p className="text-muted text-sm italic">Match with someone to begin a conversation.</p>
                            ) : (
                                <div className="space-y-2">
                                    {conversations.map((c) => (
                                        <Link
                                            key={c.match_id}
                                            to={`/messages/${c.match_id}`}
                                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
                                            data-testid={`conv-preview-${c.match_id}`}
                                        >
                                            <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-lg text-gold flex-shrink-0 relative">
                                                {c.other_user.picture ? (
                                                    <img src={c.other_user.picture} alt="" className="w-full h-full rounded-full object-cover" />
                                                ) : (
                                                    <span className="font-serif">{SIGN_GLYPH[c.other_user.sun]}</span>
                                                )}
                                                {c.unread_count > 0 && (
                                                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-gradient-to-br from-[#E6C387] to-[#F5D68F]" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-serif text-base truncate">{c.other_user.name}</p>
                                                <p className={`text-xs truncate ${c.unread_count > 0 ? "text-foreground" : "text-muted"}`}>
                                                    {c.last_message ? c.last_message.text : <span className="italic">Say hello...</span>}
                                                </p>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </motion.section>

                        {/* Daily horoscope - personalization, secondary placement */}
                        <motion.section
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="glass-gold ai-card p-6 lg:col-span-3"
                            data-testid="daily-horoscope-card"
                        >
                            <div className="flex items-baseline justify-between mb-2">
                                <div>
                                    <p className="label-tracking text-gold flex items-center gap-2">
                                        <Sparkle size={14} weight="duotone" /> Today's Reading
                                    </p>
                                    <h3 className="font-serif text-2xl mt-1">The sky for {user.sun}</h3>
                                </div>
                                <Link to="/horoscope" className="text-xs label-tracking text-muted hover:text-gold transition-colors">More →</Link>
                            </div>
                            {loadingH ? (
                                <p className="text-muted italic">Channeling the cosmos...</p>
                            ) : (
                                <p className="font-serif leading-relaxed line-clamp-6 text-[15px]" data-testid="daily-horoscope-text">
                                    {horoscope}
                                </p>
                            )}
                        </motion.section>
                    </div>

                    {/* QUICK NAV TILES */}
                    <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <QuickTile to="/matches" title="Discover" subtitle="Swipe new souls" icon={<Heart size={20} weight="duotone" />} />
                        <QuickTile to="/messages" title="Messages" subtitle={unreadTotal > 0 ? `${unreadTotal} unread` : "Stay in touch"} icon={<ChatCircle size={20} weight="duotone" />} />
                        <QuickTile to="/big-three-reading" title="Big Three" subtitle="Soul signature" icon={<Star size={20} weight="duotone" />} />
                        <QuickTile
                            to="/birth-chart"
                            title="Birth Chart"
                            subtitle={user.subscription_active ? "Your full sky" : "Unlock"}
                            icon={<Moon size={20} weight="duotone" />}
                            gold={!user.subscription_active}
                        />
                    </section>
                </div>
            </main>
        </>
    );
}

function QuickTile({ to, title, subtitle, icon, gold }) {
    return (
        <Link
            to={to}
            className={`${gold ? "glass-gold" : "glass"} p-4 hover-lift block`}
            data-testid={`quick-${title.toLowerCase().replace(/\s/g, "-")}`}
        >
            <div className="text-gold mb-2">{icon}</div>
            <p className="font-serif text-lg leading-tight">{title}</p>
            <p className={`text-xs mt-1 ${gold ? "text-gold" : "text-muted"} label-tracking`}>{subtitle}</p>
        </Link>
    );
}
